import { ApiClient } from './api-client';
import type { ApiResponse } from '../types';

export type BookingStatus =
  | 'PENDING'
  | 'CONFIRMED'
  /** Provider declined the originally requested slot and proposed a
   *  different {@code proposedDateTime}; awaiting customer accept/reject. */
  | 'COUNTER_PROPOSED'
  | 'CANCELLATION_REQUESTED'
  | 'REJECTED'
  | 'CANCELLED';
export type CancelledBy = 'CUSTOMER' | 'PROVIDER';

/** Mirrors {@code BookingsService.CANCELLATION_NOTICE_DAYS} on the server. */
export const CANCELLATION_NOTICE_DAYS = 7;
/** Mirrors {@code BookingsService.CANCELLATION_REFUND_PERCENT} on the server. */
export const CANCELLATION_REFUND_PERCENT = 50;

export interface BookingServiceLine {
  providerServiceId: string;
  serviceName: string | null;
  priceCents: number | null;
  currency: string | null;
  durationMinutes: number | null;
}

export interface BookingProviderRef {
  id: string;
  businessName: string;
  city: string | null;
  country: string | null;
}

export interface BookingCustomerRef {
  userId: string | null;
  name: string;
  email: string;
  phone: string | null;
}

export interface Booking {
  id: string;
  status: BookingStatus;
  provider: BookingProviderRef;
  customer: BookingCustomerRef;
  services: BookingServiceLine[];
  preferredDateTime: string | null;
  message: string | null;
  rejectionReason: string | null;
  cancelledBy: CancelledBy | null;
  confirmedAt: string | null;
  rejectedAt: string | null;
  cancelledAt: string | null;
  // ── Cancellation request (V13) ──────────────────────────────────────
  cancellationRequestedAt: string | null;
  cancellationRequestMessage: string | null;
  cancellationRefundCents: number | null;
  cancellationRefundCurrency: string | null;
  cancellationDecidedAt: string | null;
  cancellationDeclineReason: string | null;
  // ── Counter-proposal (V38) ──────────────────────────────────────────
  /** Set only when status === 'COUNTER_PROPOSED'. The slot the provider
   *  is suggesting in lieu of the customer's original {@link preferredDateTime}. */
  proposedDateTime: string | null;
  /** Optional short explanation from the provider with the counter-proposal. */
  proposedMessage: string | null;
  proposedAt: string | null;
  createdAt: string;
  /** True when the customer already left a review for this booking. */
  hasReview: boolean;
}

export interface CreateBookingPayload {
  customerName: string;
  customerEmail: string;
  /** Optional — backend stores null when omitted. The simplified booking
   *  flow no longer asks for a phone number (email + in-app chat are
   *  enough), but the field is kept for legacy callers. */
  customerPhone?: string;
  providerServiceIds: string[];
  /** Availability slot to claim. When set, the slot's start time becomes
   *  the booking's scheduled time and any `preferredDateTime` is ignored. */
  availabilitySlotId?: string;
  /** ISO-8601 instant. Optional fallback when no slot is selected. */
  preferredDateTime?: string;
  message?: string;
}

export interface BookingListQuery {
  status?: BookingStatus;
  page?: number;
  size?: number;
}

export const bookingsService = {
  // ── Customer ────────────────────────────────────────────────────────
  create: async (
    providerId: string,
    payload: CreateBookingPayload,
  ): Promise<Booking> =>
    (await ApiClient.post<Booking>(
      `/providers/${providerId}/booking-requests`,
      payload,
    )).data,

  listMine: (params: BookingListQuery = {}): Promise<ApiResponse<Booking[]>> =>
    ApiClient.get<Booking[], BookingListQuery>('/me/bookings', params),

  getMine: async (bookingId: string): Promise<Booking> =>
    (await ApiClient.get<Booking>(`/me/bookings/${bookingId}`)).data,

  customerCancel: async (bookingId: string): Promise<Booking> =>
    (await ApiClient.post<Booking>(`/me/bookings/${bookingId}/cancel`)).data,

  customerRequestCancellation: async (
    bookingId: string,
    message?: string,
  ): Promise<Booking> =>
    (await ApiClient.post<Booking>(
      `/me/bookings/${bookingId}/request-cancellation`,
      message ? { message } : {},
    )).data,

  /** Customer accepts the provider's counter-proposal — booking flips to
   *  CONFIRMED and preferredDateTime takes the proposedDateTime value. */
  customerAcceptCounter: async (bookingId: string): Promise<Booking> =>
    (await ApiClient.post<Booking>(`/me/bookings/${bookingId}/accept-counter`)).data,

  /** Customer rejects the provider's counter-proposal — booking ends in REJECTED. */
  customerRejectCounter: async (bookingId: string): Promise<Booking> =>
    (await ApiClient.post<Booking>(`/me/bookings/${bookingId}/reject-counter`)).data,

  // ── Provider ────────────────────────────────────────────────────────
  listIncoming: (params: BookingListQuery = {}): Promise<ApiResponse<Booking[]>> =>
    ApiClient.get<Booking[], BookingListQuery>(
      '/providers/me/booking-requests',
      params,
    ),

  getIncoming: async (bookingId: string): Promise<Booking> =>
    (await ApiClient.get<Booking>(
      `/providers/me/booking-requests/${bookingId}`,
    )).data,

  providerConfirm: async (bookingId: string): Promise<Booking> =>
    (await ApiClient.post<Booking>(
      `/providers/me/booking-requests/${bookingId}/confirm`,
    )).data,

  providerReject: async (bookingId: string, reason?: string): Promise<Booking> =>
    (await ApiClient.post<Booking>(
      `/providers/me/booking-requests/${bookingId}/reject`,
      reason ? { reason } : {},
    )).data,

  /** Provider counter-proposes a different slot. Triggers a transition
   *  PENDING → COUNTER_PROPOSED and posts a chat message in the booking's
   *  conversation. {@code proposedDateTime} must be an ISO-8601 string in
   *  the future. */
  providerProposeNewDate: async (
    bookingId: string,
    proposedDateTime: string,
    message?: string,
  ): Promise<Booking> =>
    (await ApiClient.post<Booking>(
      `/providers/me/booking-requests/${bookingId}/propose-date`,
      { proposedDateTime, message: message || undefined },
    )).data,

  providerCancel: async (bookingId: string): Promise<Booking> =>
    (await ApiClient.post<Booking>(
      `/providers/me/booking-requests/${bookingId}/cancel`,
    )).data,

  providerApproveCancellation: async (bookingId: string): Promise<Booking> =>
    (await ApiClient.post<Booking>(
      `/providers/me/booking-requests/${bookingId}/approve-cancellation`,
    )).data,

  providerDeclineCancellation: async (
    bookingId: string,
    reason?: string,
  ): Promise<Booking> =>
    (await ApiClient.post<Booking>(
      `/providers/me/booking-requests/${bookingId}/decline-cancellation`,
      reason ? { reason } : {},
    )).data,
};
